// The DOS Live Activity native seam — the JS half of the CUSTOM ActivityKit
// Capacitor plugin (no stock plugin exists). iOS-only + web/Android no-op, mirroring push.ts: a Live Activity
// is an iOS lock-screen surface, so every entry returns null off iOS behind a capability check. Kept
// app-agnostic (no @cntrl/api-client / config import) — the caller builds the device-computed plan and POSTs
// the returned tokens in its own body shape, exactly as push.ts hands the raw APNs token to its caller.
//
// Since ART-208 went automatic there is no "start" button: the app prepares every eligible show (the server then
// push-starts the card at T-1h) and starts it locally only when the show is already within that window. Every
// call here SETTLES — the native side bounds its token waits — and a native failure is logged, never thrown.
import { registerPlugin } from "@capacitor/core";
import { getPlatform, isNative } from "./platform.js";

/** The native plugin contract the Swift side implements (ios/App custom plugin). ContentState/attributes are
 * opaque here — the device-computed shapes agreed with the widget's Codable structs. */
interface LiveActivityPlugin {
  /** ActivityKit available AND the user has Live Activities enabled for the app. */
  isSupported(): Promise<{ supported: boolean }>;
  /** Start (or update) an activity locally and return its per-activity push-update token, or null if Apple did not
   *  issue one within a few seconds (it then reaches the server through the token sink). */
  start(options: StartLiveActivityInput): Promise<{ activityToken: string | null; sinkKey?: string | null }>;
  /** The push-to-start token (per activity type per install), or null if none arrived in time. */
  pushToStartToken(): Promise<{ token: string | null }>;
  /** The install's stable device key + this show's token-sink key, without starting anything. */
  prepare(options: { showId: string; sinkUrl?: string; appEnv?: string }): Promise<{
    deviceKey: string;
    sinkKey: string | null;
  }>;
  /** End this show's card on this phone now. */
  end(options: { showId: string }): Promise<void>;
  isMuted(options: { showId: string }): Promise<{ muted: boolean }>;
  setMuted(options: { showId: string; muted: boolean }): Promise<void>;
}

const LiveActivity = registerPlugin<LiveActivityPlugin>("LiveActivity");

/** The initial state to start a Live Activity with (the device-computed opaque shapes + the stale horizon). */
export interface StartLiveActivityInput {
  attributesType: string;
  attributes: unknown;
  contentState: unknown;
  staleDateEpochSec?: number;
  /** The ABSOLUTE token-sink URL. When the server push-starts a card on a closed, locked phone, the native
   *  side reports the new card's update token here with a per-show key it mints — the session is unreadable then. */
  sinkUrl?: string;
  /** production | sandbox — the APNs environment the sink reports under. */
  appEnv?: string;
}

/** What the server needs to drive this phone's card for one show. */
export interface PreparedLiveActivity {
  /** This install's stable key — the plan row the server keeps for this phone. */
  deviceKey: string;
  /** This show's token-sink key, registered (hashed) with the plan. */
  sinkKey: string | null;
  /** ActivityKit `pushToStartToken` (per install) — the server's T-1h `start` push target. */
  startToken: string | null;
}

/** True on a platform that can host a Live Activity (iOS native). Web/Android → false. */
export function liveActivitySupported(): boolean {
  return isNative() && getPlatform() === "ios";
}

function warn(what: string, err: unknown): void {
  console.warn(`[live-activity] ${what} failed:`, err instanceof Error ? err.message : err);
}

/** iOS native AND the user has Live Activities switched on for the app. */
export async function liveActivitiesEnabled(): Promise<boolean> {
  if (!liveActivitySupported()) return false;
  try {
    return (await LiveActivity.isSupported()).supported;
  } catch (err) {
    warn("isSupported", err);
    return false;
  }
}

/** The device key, sink key and push-to-start token for one show — everything a registration needs except the plan.
 *  Null off iOS or when the native side is unreachable. */
export async function prepareLiveActivity(options: {
  showId: string;
  sinkUrl?: string;
  appEnv?: string;
}): Promise<PreparedLiveActivity | null> {
  if (!liveActivitySupported()) return null;
  try {
    const [prepared, start] = await Promise.all([
      LiveActivity.prepare(options),
      LiveActivity.pushToStartToken().catch((err: unknown) => {
        warn("pushToStartToken", err);
        return { token: null };
      }),
    ]);
    return { deviceKey: prepared.deviceKey, sinkKey: prepared.sinkKey ?? null, startToken: start.token ?? null };
  } catch (err) {
    warn("prepare", err);
    return null;
  }
}

/** Put the card on the lock screen now (or update the one already there). Returns its update token, null when Apple
 *  issued none in time, or undefined when the start itself failed. */
export async function startLiveActivityLocally(start: StartLiveActivityInput): Promise<string | null | undefined> {
  if (!liveActivitySupported()) return undefined;
  try {
    return (await LiveActivity.start(start)).activityToken ?? null;
  } catch (err) {
    warn("start", err);
    return undefined;
  }
}

/** End this show's card on this phone. */
export async function endLiveActivity(showId: string): Promise<void> {
  if (!liveActivitySupported()) return;
  try {
    await LiveActivity.end({ showId });
  } catch (err) {
    warn("end", err);
  }
}

/** Whether this phone has muted the show's card. */
export async function isLiveActivityMuted(showId: string): Promise<boolean> {
  if (!liveActivitySupported()) return false;
  try {
    return (await LiveActivity.isMuted({ showId })).muted;
  } catch (err) {
    warn("isMuted", err);
    return false;
  }
}

export async function setLiveActivityMuted(showId: string, muted: boolean): Promise<void> {
  if (!liveActivitySupported()) return;
  try {
    await LiveActivity.setMuted({ showId, muted });
  } catch (err) {
    warn("setMuted", err);
  }
}
